import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

type Preview = ReactNode | null;

const PreviewContext = createContext<{
  previewTarget: HTMLDivElement | null;
  setRenderPreview: Dispatch<boolean>;
} | null>(null);

type RenderProps = {
  preview: Preview;
};

export const PreviewRenderer = ({ children }: { children: ({ preview }: RenderProps) => ReactElement }) => {
  const [renderPreview, setRenderPreview] = useState(false);

  const [previewTarget, setPreviewTarget] = useState<HTMLDivElement | null>(null);

  const preview = renderPreview ? (
    <div ref={setPreviewTarget} className='journey-experience-node child:rounded-lg' />
  ) : null;

  return (
    <PreviewContext.Provider value={{ setRenderPreview, previewTarget }}>
      {children({ preview })}
    </PreviewContext.Provider>
  );
};

export const JourneyEditorPreview = ({ children }: PropsWithChildren<{}>) => {
  const context = useContext(PreviewContext);
  if (!context) throw new Error('JourneyEditorPreview must be rendered within a PreviewRenderer');

  const { previewTarget, setRenderPreview } = context;

  const renderPreview = children != null;

  useEffect(() => {
    setRenderPreview(renderPreview);

    return () => {
      setRenderPreview(false);
    };
  }, [renderPreview, setRenderPreview]);

  if (!previewTarget) return null;
  return createPortal(children, previewTarget);
};
